import { Modal, Stack, Button, useMantineColorScheme, Box, Group, LoadingOverlay } from "@mantine/core";
import { VariableInput } from "../VariableInput";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    setVariableValues,
    selectReportFull
} from "@/store/slices/reportSlice";

interface ReportRunVariablesModalProps {
    opened: boolean;
    onClose: () => void;
    onRun: (values: Record<string, string>) => void;
}

export function ReportRunVariablesModal({
    opened,
    onClose,
    onRun,
}: ReportRunVariablesModalProps) {
    const dispatch = useAppDispatch();
    const {
        report,
        requiredVariables,
        variableValues,
        isExecuting
    } = useAppSelector(selectReportFull);
    const { colorScheme } = useMantineColorScheme();

    if (!report || requiredVariables.length === 0) return null;

    const handleRun = () => {
        onRun(variableValues);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Run Report with Variables"
            centered
            overlayProps={{
                backgroundOpacity: 0.1,
                blur: 2,
            }}
        >
            <LoadingOverlay visible={isExecuting} overlayProps={{ backgroundOpacity: 0.1, blur: 2 }} />
            <Stack gap="md">
                <Box
                    p="md"
                    style={{
                        border: '1px solid var(--modal-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                        background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined
                    }}
                >
                    <Stack gap="md">
                        {requiredVariables.map((varName) => (
                            <VariableInput
                                key={varName}
                                name={varName}
                                config={(report.variables || []).find(v => v.name === varName)}
                                value={variableValues[varName] || ""}
                                onChange={(val: string) => dispatch(setVariableValues({ ...variableValues, [varName]: val }))}
                                dataSourceId={report.dataSourceId}
                            />
                        ))}
                    </Stack>
                </Box>

                <Group justify="flex-end" pt="md">
                    <Button 
                        variant={colorScheme === 'dark' ? 'light' : 'subtle'} 
                        color="gray" 
                        onClick={onClose} 
                        disabled={isExecuting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRun}
                        loading={isExecuting}
                        color={colorScheme === 'dark' ? 'gray.8' : 'brand'}
                        variant="filled"
                    >
                        Run Report
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
